import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import {
  AddressFields,
  FIELD_CLASS,
  type AddressValue,
} from '@/components/properties/AddressFields';
import {
  useCreateTechnicianProfile,
  useUpdateTechnicianProfile,
  type TechnicianProfile,
} from '@/routes/technicians/useTechnicianProfile';

const EMPTY_ADDRESS: AddressValue = { line1: '', line2: '', city: '', state: '', postalCode: '' };

/**
 * Create-or-edit, one form (M1-24) — owner/admin only, enforced server-side
 * (`users.manage`, same gate as `PropertyEndpoints.Create`/`Update`). Rates
 * are plain inputs here on purpose: the person who can even OPEN this dialog
 * already holds `users.manage`, which is exactly who the API lets see them.
 *
 * The address block only renders when Start location is Home — a Shop-based
 * technician has no address to enter, matching `TechnicianProfile.HomeAddress`
 * being genuinely absent (not an empty one) on the API side.
 */
export function TechnicianProfileFormDialog({
  userId,
  userName,
  profile,
  onClose,
  onSaved,
}: {
  userId: string;
  userName: string;
  /** Absent for create. */
  profile?: TechnicianProfile;
  onClose: () => void;
  onSaved: (announcement: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateTechnicianProfile();
  // Called unconditionally (Rules of Hooks) with a fallback id that is never
  // actually used in create mode — `submit` below dispatches to `create`
  // instead whenever `profile` is absent.
  const update = useUpdateTechnicianProfile(profile?.id ?? '');
  const mutation = profile ? update : create;

  const [shortBio, setShortBio] = useState(profile?.shortBio ?? '');
  const [hireDate, setHireDate] = useState(profile?.hireDate ?? '');
  const [employeeRef, setEmployeeRef] = useState(profile?.employeeRef ?? '');
  const [startLocation, setStartLocation] = useState<'Shop' | 'Home'>(
    (profile?.startLocation as 'Shop' | 'Home' | undefined) ?? 'Shop',
  );
  const [homeAddress, setHomeAddress] = useState<AddressValue>(
    profile?.homeAddress
      ? {
          line1: profile.homeAddress.line1,
          line2: profile.homeAddress.line2 ?? '',
          city: profile.homeAddress.city,
          state: profile.homeAddress.state,
          postalCode: profile.homeAddress.postalCode,
        }
      : EMPTY_ADDRESS,
  );
  const [costRateHourly, setCostRateHourly] = useState(String(profile?.costRateHourly ?? 0));
  // M2-33: an OVERRIDE, empty unless set — empty bills this technician's
  // time at the price book's labor rate. It used to default to "0", which the
  // M2-33 migration had to turn back into "not set".
  const [billableRateHourly, setBillableRateHourly] = useState(
    profile?.billableRateHourly == null ? '' : String(profile.billableRateHourly),
  );
  const [isOnCall, setIsOnCall] = useState(profile?.isOnCall ?? false);
  const [emergencyContactName, setEmergencyContactName] = useState(
    profile?.emergencyContactName ?? '',
  );
  const [emergencyContactPhone, setEmergencyContactPhone] = useState(
    profile?.emergencyContactPhone ?? '',
  );
  const [notes, setNotes] = useState(profile?.notes ?? '');

  const homeAddressComplete =
    startLocation !== 'Home' ||
    (homeAddress.line1.trim() !== '' &&
      homeAddress.city.trim() !== '' &&
      homeAddress.state.trim() !== '' &&
      homeAddress.postalCode.trim() !== '');
  // The API's own ceiling (`[Range(0, 10000)]` on both rate parameters), said
  // here so the office hears it before the round trip rather than as a 400.
  //
  // ⚠️ NOT the schema's 99,999.99 bound. That one is the outer limit every
  // money column shares (see Money.Max); this is the tighter business rule for
  // an hourly rate, and it is the one a person hits first.
  const MAX_RATE = 10000;
  const COST_RATE_ERROR_ID = 'technician-cost-rate-error';
  const BILLABLE_RATE_ERROR_ID = 'technician-billable-rate-error';
  const BILLABLE_RATE_HINT_ID = 'technician-billable-rate-hint';

  // ⚠️ `Number('')` is 0, not NaN. The previous `Number(x) >= 0` therefore
  // accepted a CLEARED rate field and saved it as $0.00 — a silent wrong
  // number rather than a refusal, on a field that decides payroll cost and
  // customer billing. Checking the string is empty is what closes that.
  const rateInvalid = (raw: string) => {
    if (raw.trim() === '') {
      return true;
    }
    const value = Number(raw);
    return Number.isNaN(value) || value < 0 || value > MAX_RATE;
  };

  // M2-33: the override may be EMPTY (the price book's rate), but when set it
  // is more than zero — the API refuses 0, which would bill the technician's
  // time at $0.00.
  const overrideInvalid = (raw: string) => {
    if (raw.trim() === '') {
      return false;
    }
    const value = Number(raw);
    return Number.isNaN(value) || value <= 0 || value > MAX_RATE;
  };

  // ⚠️ A fraction of a cent (95.555) is refused by the API ("A billable rate
  // must be a whole number of cents."), and this dialog answers any refused
  // save with a reasonless "couldn't save" — so the rule is said here, on the
  // field. Judged on the number, not the string (95.550 is whole cents), with
  // a tolerance: 64.35 * 100 is 6434.999999999999 in floating point.
  const overrideFractionOfCent = (raw: string) => {
    const cents = Number(raw) * 100;
    return raw.trim() !== '' && Math.abs(cents - Math.round(cents)) > 1e-6;
  };

  const costRateInvalid = rateInvalid(costRateHourly);
  const billableRateOutOfRange = overrideInvalid(billableRateHourly);
  const billableRateInvalid = billableRateOutOfRange || overrideFractionOfCent(billableRateHourly);
  const ratesValid = !costRateInvalid && !billableRateInvalid;
  const canSubmit = homeAddressComplete && ratesValid;

  function submit() {
    // The disabled Save button is the only thing stopping this today — every
    // other dialog guards the handler too, and a keyboard or test path that
    // calls submit() directly should not get past the same rule.
    if (!canSubmit) {
      return;
    }

    const body = {
      shortBio: shortBio.trim() || null,
      hireDate: hireDate || null,
      employeeRef: employeeRef.trim() || null,
      startLocation,
      homeAddress:
        startLocation === 'Home'
          ? {
              line1: homeAddress.line1.trim(),
              line2: homeAddress.line2.trim() || null,
              city: homeAddress.city.trim(),
              state: homeAddress.state.trim(),
              postalCode: homeAddress.postalCode.trim(),
            }
          : null,
      costRateHourly: Number(costRateHourly),
      billableRateHourly: billableRateHourly.trim() === '' ? null : Number(billableRateHourly),
      isOnCall,
      emergencyContactName: emergencyContactName.trim() || null,
      emergencyContactPhone: emergencyContactPhone.trim() || null,
      notes: notes.trim() || null,
    };

    const onSuccess = () => {
      onSaved(
        profile
          ? t('technicianProfile.form.saved', { name: userName })
          : t('technicianProfile.form.created', { name: userName }),
      );
      onClose();
    };

    if (profile) {
      update.mutate(body, { onSuccess });
    } else {
      create.mutate({ userId, ...body }, { onSuccess });
    }
  }

  const textarea =
    'w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

  return (
    <Dialog
      titleId="technician-profile-form-title"
      title={
        profile
          ? t('technicianProfile.form.editTitle', { name: userName })
          : t('technicianProfile.form.createTitle', { name: userName })
      }
      onClose={onClose}
      testId="technician-profile-form-dialog"
      dismissLabel={t('technicianProfile.form.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending
              ? t('technicianProfile.form.saving')
              : t('technicianProfile.form.confirm')}
          </Button>
        </>
      }
    >
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('technicianProfile.form.shortBio')}</span>
        <textarea
          rows={2}
          className={textarea}
          value={shortBio}
          onChange={(event) => setShortBio(event.target.value)}
        />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('technicianProfile.form.hireDate')}</span>
          <input
            type="date"
            className={FIELD_CLASS}
            value={hireDate}
            onChange={(event) => setHireDate(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('technicianProfile.form.employeeRef')}</span>
          <input
            className={FIELD_CLASS}
            value={employeeRef}
            onChange={(event) => setEmployeeRef(event.target.value)}
          />
        </label>
      </div>

      <fieldset className="text-sm">
        <legend className="mb-1 font-medium">{t('technicianProfile.form.startLocation')}</legend>
        <div className="flex gap-4">
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="startLocation"
              checked={startLocation === 'Shop'}
              onChange={() => setStartLocation('Shop')}
            />
            {t('technicianProfile.form.startLocationShop')}
          </label>
          <label className="flex items-center gap-1.5">
            <input
              type="radio"
              name="startLocation"
              checked={startLocation === 'Home'}
              onChange={() => setStartLocation('Home')}
            />
            {t('technicianProfile.form.startLocationHome')}
          </label>
        </div>
      </fieldset>

      {startLocation === 'Home' ? (
        <AddressFields
          value={homeAddress}
          onChange={setHomeAddress}
          labels={{
            line1: t('technicianProfile.form.line1'),
            line2: t('technicianProfile.form.line2'),
            city: t('technicianProfile.form.city'),
            state: t('technicianProfile.form.state'),
            postalCode: t('technicianProfile.form.postalCode'),
          }}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">
            {t('technicianProfile.form.costRateHourly')}
          </span>
          <input
            type="number"
            min={0}
            max={MAX_RATE}
            step="0.01"
            aria-invalid={costRateInvalid ? true : undefined}
            aria-describedby={costRateInvalid ? COST_RATE_ERROR_ID : undefined}
            className={FIELD_CLASS}
            value={costRateHourly}
            onChange={(event) => setCostRateHourly(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">
            {t('technicianProfile.form.billableRateHourly')}
          </span>
          <input
            type="number"
            min={0.01}
            max={MAX_RATE}
            step="0.01"
            placeholder={t('technicianProfile.form.billableRatePlaceholder')}
            aria-invalid={billableRateInvalid ? true : undefined}
            aria-describedby={billableRateInvalid ? BILLABLE_RATE_ERROR_ID : BILLABLE_RATE_HINT_ID}
            className={FIELD_CLASS}
            value={billableRateHourly}
            onChange={(event) => setBillableRateHourly(event.target.value)}
          />
        </label>
      </div>

      {/* The refusal said out loud rather than left to a greyed-out button the
          user has to guess about — the shape TechnicianLicenseDialog settled on.
          `role="alert"` so a screen-reader user hears why Save stopped working,
          and the id is what aria-describedby above points at. ⚠️ Each id must
          render whenever its aria-describedby is set, or the jest-axe checks in
          TechnicianProfileDetail.test.tsx go red. */}
      {billableRateInvalid ? null : (
        <p id={BILLABLE_RATE_HINT_ID} className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          {t('technicianProfile.form.billableRateHint')}
        </p>
      )}
      {costRateInvalid ? (
        <p
          id={COST_RATE_ERROR_ID}
          role="alert"
          className="mt-1 text-sm text-[var(--color-destructive)]"
        >
          {t('technicianProfile.form.costRateOutOfRange', {
            max: MAX_RATE.toLocaleString('en-US'),
          })}
        </p>
      ) : null}
      {billableRateInvalid ? (
        <p
          id={BILLABLE_RATE_ERROR_ID}
          role="alert"
          className="mt-1 text-sm text-[var(--color-destructive)]"
        >
          {billableRateOutOfRange
            ? t('technicianProfile.form.billableRateOutOfRange', {
                max: MAX_RATE.toLocaleString('en-US'),
              })
            : t('technicianProfile.form.billableRateWholeCents')}
        </p>
      ) : null}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isOnCall}
          onChange={(event) => setIsOnCall(event.target.checked)}
        />
        {t('technicianProfile.form.isOnCall')}
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">
            {t('technicianProfile.form.emergencyContactName')}
          </span>
          <input
            className={FIELD_CLASS}
            value={emergencyContactName}
            onChange={(event) => setEmergencyContactName(event.target.value)}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">
            {t('technicianProfile.form.emergencyContactPhone')}
          </span>
          <input
            className={FIELD_CLASS}
            value={emergencyContactPhone}
            onChange={(event) => setEmergencyContactPhone(event.target.value)}
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('technicianProfile.form.notes')}</span>
        <textarea
          rows={2}
          className={textarea}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      {mutation.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('technicianProfile.form.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
